import { createContext, useContext, useMemo, useReducer, type Dispatch, type ReactNode } from "react";

import { getAppConfig, getAppConfigError } from "@/lib/app-config";
import type { AppConfig, SearchResponse, SearchStep, UiError, ViewStatus } from "@/lib/types";

interface SearchSessionState {
  appConfig: AppConfig | null;
  configError: string | null;
  query: string;
  maxReposInput: string;
  searchId: string | null;
  status: ViewStatus;
  currentStep: SearchStep | null;
  response: SearchResponse | null;
  formError: string | null;
  error: UiError | null;
  infoMessage: string | null;
  turnstileToken: string | null;
  showTurnstile: boolean;
  highlightedRepo: string | null;
  highlightedContributor: string | null;
}

type SearchSessionAction =
  | { type: "SET_QUERY"; query: string }
  | { type: "SET_MAX_REPOS"; value: string }
  | { type: "SET_TURNSTILE_TOKEN"; token: string | null }
  | { type: "SET_TURNSTILE_VISIBLE"; visible: boolean }
  | { type: "START_SUBMIT"; query: string; searchId: string }
  | { type: "START_RECOVERY"; searchId: string }
  | { type: "HYDRATE_RESPONSE"; response: SearchResponse }
  | { type: "SET_FORM_ERROR"; message: string | null }
  | { type: "SET_FAILURE"; error: UiError; searchId?: string | null }
  | { type: "SET_INFO"; message: string | null }
  | { type: "CLEAR_SEARCH" }
  | { type: "SET_HIGHLIGHT"; repo?: string | null; contributor?: string | null };

const initialConfig = getAppConfig();
const initialConfigError = getAppConfigError();

const initialState: SearchSessionState = {
  appConfig: initialConfig,
  configError: initialConfigError,
  query: "",
  maxReposInput: "8",
  searchId: null,
  status: "idle",
  currentStep: null,
  response: null,
  formError: null,
  error: null,
  infoMessage: null,
  turnstileToken: null,
  showTurnstile: false,
  highlightedRepo: null,
  highlightedContributor: null,
};

function reducer(state: SearchSessionState, action: SearchSessionAction): SearchSessionState {
  switch (action.type) {
    case "SET_QUERY":
      return { ...state, query: action.query, formError: null, infoMessage: null };
    case "SET_MAX_REPOS":
      return { ...state, maxReposInput: action.value, formError: null };
    case "SET_TURNSTILE_TOKEN":
      return { ...state, turnstileToken: action.token, formError: action.token ? null : state.formError };
    case "SET_TURNSTILE_VISIBLE":
      return { ...state, showTurnstile: action.visible };
    case "START_SUBMIT":
      return {
        ...state,
        query: action.query,
        searchId: action.searchId,
        status: "submitting",
        currentStep: null,
        response: null,
        error: null,
        formError: null,
        infoMessage: null,
        turnstileToken: null,
        showTurnstile: false,
        highlightedRepo: null,
        highlightedContributor: null,
      };
    case "START_RECOVERY":
      return {
        ...state,
        searchId: action.searchId,
        status: "pending",
        error: null,
        infoMessage: null,
        showTurnstile: false,
      };
    case "HYDRATE_RESPONSE":
      return {
        ...state,
        query: action.response.query,
        searchId: action.response.search_id,
        status: action.response.status,
        currentStep: action.response.current_step,
        response: action.response,
        error: null,
        formError: null,
        infoMessage: null,
        turnstileToken: null,
        showTurnstile: false,
        highlightedRepo: null,
        highlightedContributor: null,
      };
    case "SET_FORM_ERROR":
      return { ...state, formError: action.message };
    case "SET_FAILURE":
      return {
        ...state,
        searchId: action.searchId ?? state.searchId,
        status: "failed",
        currentStep: "complete",
        error: action.error,
        turnstileToken: null,
        showTurnstile: false,
      };
    case "SET_INFO":
      return { ...state, infoMessage: action.message };
    case "CLEAR_SEARCH":
      return {
        ...state,
        searchId: null,
        status: "idle",
        currentStep: null,
        response: null,
        error: null,
        formError: null,
        infoMessage: null,
        turnstileToken: null,
        showTurnstile: false,
        highlightedRepo: null,
        highlightedContributor: null,
      };
    case "SET_HIGHLIGHT":
      if ((action.repo ?? null) === state.highlightedRepo && (action.contributor ?? null) === state.highlightedContributor) {
        return state;
      }

      return {
        ...state,
        highlightedRepo: action.repo ?? null,
        highlightedContributor: action.contributor ?? null,
      };
    default:
      return state;
  }
}

const SearchSessionContext = createContext<{ state: SearchSessionState; dispatch: Dispatch<SearchSessionAction> } | null>(null);

export function SearchSessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <SearchSessionContext.Provider value={value}>{children}</SearchSessionContext.Provider>;
}

export function useSearchSession() {
  const context = useContext(SearchSessionContext);
  if (!context) {
    throw new Error("useSearchSession must be used within SearchSessionProvider");
  }

  return context;
}
